import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AdminRoleLabels } from "entities/auth/constants";
import useRolePermission from "entities/auth/hooks/useRolePermission";
import { AdminRole } from "entities/auth/types";
import { ChangeEvent, FC, useMemo } from "react";
import Drawer from "shared/components/Drawer";
import ContentSection from "shared/components/Drawer/components/ContentSection";
import AutocompleteField from "shared/components/Form/AutocompleteField";
import TextField from "shared/components/Form/TextField";
import FieldLabel from "shared/components/Form/components/FieldLabel";
import { COLOR_BLACK, COLOR_GREY_6 } from "shared/theme/colors";
import { CheckBoxIcon } from "shared/theme/icons/CheckBoxIcon";
import { CheckedBoxIcon } from "shared/theme/icons/CheckedBoxIcon";
import { useRolesAndPermissionsPage } from "../../context";
import { StyledButton } from "../../styled";
import { roleOptions } from "./constants";
import { AdminDetailsProvider, useAdminDetails } from "./context";

const AdminDetailsContainer = () => (
  <AdminDetailsProvider>
    <AdminDetails />
  </AdminDetailsProvider>
);

const AdminDetails: FC = () => {
  const { adminDetailsDrawer } = useRolesAndPermissionsPage();
  const {
    handleCloseDrawer,
    emailInput,
    roles,
    setRoles,
    isLoading,
    isPending,
    isDisabled,
    handleSaveAdmin,
  } = useAdminDetails();

  const writeAccess = useRolePermission("brandsAndPages:write");
  const selectedRoles = useMemo(
    () =>
      roles.map((role) => ({
        id: role,
        name: AdminRoleLabels[role].label,
      })),
    [roles],
  );

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    emailInput.input.onChange(e);
    emailInput.setErrors([]);
  };

  const handleRoleChange = (values: { id: string; name: string }[]) => {
    const updatedRoles = values.map((item) => item.id as AdminRole);
    setRoles(updatedRoles);
  };

  const title = adminDetailsDrawer.drawerDetails ? "Edit User" : "Add New User";

  return (
    <Drawer
      title={title}
      isOpen={adminDetailsDrawer.isOpen}
      onCloseDrawer={handleCloseDrawer}
      headingRight={
        writeAccess.allowed && (
          <StyledButton
            title="Save"
            size="small"
            loading={isLoading || isPending}
            disabled={isDisabled}
            onClick={handleSaveAdmin}
          />
        )
      }
    >
      <ContentSection title="General">
        <Stack spacing="1rem">
          <TextField
            id={emailInput.input.id}
            label="Email*"
            placeholder="Email"
            fullWidth
            required
            labelColor={COLOR_BLACK}
            value={emailInput.input.value}
            errors={emailInput.input.errors}
            onChange={handleEmailChange}
            onBlur={emailInput.input.onBlur}
          />
          <AutocompleteField
            id="roles-autocomplete"
            multiple
            disableCloseOnSelect
            borderColor={COLOR_GREY_6}
            options={roleOptions}
            value={selectedRoles}
            onChange={(_, val) => handleRoleChange(val)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, { selected }) => {
              return (
                <li {...props} key={props.id}>
                  <Checkbox
                    icon={<CheckBoxIcon />}
                    checkedIcon={<CheckedBoxIcon />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    id={props.id}
                  />
                  <Typography color={COLOR_BLACK} variant="bodyMRegular">
                    {option.name}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <>
                <FieldLabel label="Roles*" labelColor={COLOR_BLACK} />
                <TextField {...params} />
              </>
            )}
          />
        </Stack>
      </ContentSection>
    </Drawer>
  );
};

export default AdminDetailsContainer;
